import { ApolloError } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react/hooks/useLazyQuery'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import { useCallback } from 'react'

import {
    GetFamilyElementChanges,
    GetProductTypeChanges,
} from 'api/graphQL/queries/changes.queries'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import { useParams } from 'react-router-dom'
import { useTrackChangesUpdateShallowSelector } from '../services'
import { normalizeTrackChanges } from '../utils/TrackChangesAndSuggestionsUtils'

interface TrackChangesQueryResponse {
    getProjectProductFamily?: {
        trackChanges: TrackedChange[]
    }
    getProjectNugget?: {
        trackChanges: TrackedChange[]
    }
}

interface GetTrackChangesQueryVariables {
    input: { projectId: string; elementId: string }
    includeProductTypeInFamilyTrackChanges?: boolean
}

/**
 * A custom hook that provides functionality to fetch and manage TrackChanges data.
 *
 * @returns {Object} An object containing the `fetchTrackChangesData` function.
 * @returns {Function} returns.fetchTrackChangesData - A function to fetch the project data.
 */
export const useTrackChangesQuery = (): {
    fetchTrackChangesData: () => Promise<void>
} => {
    const notification = useNotification()
    const { projectId = '' } = useParams<{ projectId: string }>()
    const {
        isProjectProductFamily,
        elementId,
        isProductTypeProject,
    } = useElementItemShallowSelector((s) => ({
        isProjectProductFamily: s.elementItemValue.isProjectProductFamily,
        elementId: s.elementItemValue.elementId,
        isProductTypeProject: s.elementItemValue.isProductTypeProject,
    }))
    const {
        setTrackChangesData,
        setIsSuggestionsDataUpdated,
        trackChangesData,
        isSuggestionsDataUpdated,
    } = useSuggestionsShallowSelector((s) => ({
        setTrackChangesData: s.setTrackChangesData,
        trackChangesData: s.trackChangesData,
        setIsSuggestionsDataUpdated: s.setIsSuggestionsDataUpdated,
        isSuggestionsDataUpdated: s.isSuggestionsDataUpdated,
    }))
    const { setIsTrackedChangesFetched } = useTrackChangesUpdateShallowSelector(
        (s) => ({
            setIsTrackedChangesFetched: s.setIsTrackedChangesFetched,
        })
    )
    const showError = (error: ApolloError) => {
        notification.error({ message: error.message })
        console.error(
            summarizeApolloError(error, 'WorkspaceEditor-getTrackChangesQuery')
        )
    }

    const handleSuccess = useCallback(
        (response: TrackChangesQueryResponse | null) => {
            const trackChanges =
                response?.getProjectProductFamily?.trackChanges ??
                response?.getProjectNugget?.trackChanges

            if (!trackChanges?.length) return

            const {
                trackChangesDataWithId,
                removeTrackChangesId,
            } = normalizeTrackChanges(trackChanges, trackChangesData)

            if (Object.keys(trackChangesDataWithId).length) {
                setTrackChangesData(
                    trackChangesDataWithId,
                    removeTrackChangesId
                )
            }
        },
        [trackChangesData, setTrackChangesData]
    )

    const createTrackChangesQuery = (query: any, label: string) =>
        useLazyQuery(query, {
            displayName: label,
            fetchPolicy: 'no-cache',
            onCompleted: (data) => {
                handleSuccess(data)

                if (!isSuggestionsDataUpdated) {
                    setIsTrackedChangesFetched?.(true)
                }
                setIsSuggestionsDataUpdated(false)
            },
            onError: (error) => {
                showError(error)
                setIsSuggestionsDataUpdated(false)
                setIsTrackedChangesFetched?.(false)
            },
        })

    const [fetchFamilyChanges] = createTrackChangesQuery(
        GetFamilyElementChanges,
        'GetFamilyChanges'
    )
    const [fetchNuggetChanges] = createTrackChangesQuery(
        GetProductTypeChanges,
        'GetNuggetChanges'
    )

    const fetchTrackChangesData = useCallback(async () => {
        const variables: GetTrackChangesQueryVariables = {
            input: {
                projectId,
                elementId,
            },
            ...(isProductTypeProject
                ? {}
                : {
                      includeProductTypeInFamilyTrackChanges: isProjectProductFamily,
                  }),
        }

        const queryFn = isProductTypeProject
            ? fetchNuggetChanges
            : fetchFamilyChanges
        return queryFn({ variables })
    }, [
        projectId,
        elementId,
        isProductTypeProject,
        isProjectProductFamily,
        isProductTypeProject,
        fetchFamilyChanges,
        fetchNuggetChanges,
    ])

    return { fetchTrackChangesData }
}
